/**
 * blueprintStateStore
 *
 * Holds the live `blueprintDataTables` per blueprint folder so any surface
 * (toolbar, dialogs) always reads the freshest selection without requiring
 * a re-mount. The Setup dialog's Save action calls `invalidate(folderPath)`
 * which reloads the manifest via the `readBlueprintMetadata` IPC — the
 * toolbar then re-reads without a folderPath-change cycle.
 *
 * Design:
 *   - `blueprintDataTablesByFolder` — Record<folderPath, BlueprintDataTable[]>
 *   - `invalidate(folderPath)` — async; calls IPC then updates the map
 *   - `useBlueprintDataTables(folderPath)` — selector hook (useShallow)
 *
 * The `_readMetadata` field is injectable for unit tests; the default
 * implementation calls `window.electronAPI.databaseBlueprint.readMetadata`.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { BlueprintDataTable, ReadBlueprintMetadataResult } from '../../../database-blueprint/types/databaseBlueprintTypes';

type ReadMetadataFn = (req: { folderPath: string }) => Promise<ReadBlueprintMetadataResult>;

type BlueprintStateStore = {
  blueprintDataTablesByFolder: Record<string, BlueprintDataTable[]>;

  /** Reload the manifest for `folderPath` and update the store. */
  invalidate: (folderPath: string) => Promise<void>;

  /**
   * Internal — injectable IPC function so unit tests can bypass
   * `window.electronAPI`. Defaults to the real IPC call on first use.
   */
  _readMetadata: ReadMetadataFn | null;
};

function defaultReadMetadata(req: { folderPath: string }): Promise<ReadBlueprintMetadataResult> {
  const api = window.electronAPI?.databaseBlueprint;
  if (!api) {
    return Promise.resolve({ success: false, error: 'electronAPI unavailable' });
  }
  return api.readMetadata(req);
}

export const useBlueprintStateStore = create<BlueprintStateStore>((set, get) => ({
  blueprintDataTablesByFolder: {},
  _readMetadata: null,

  invalidate: async (folderPath: string): Promise<void> => {
    const readFn = get()._readMetadata ?? defaultReadMetadata;
    try {
      const result = await readFn({ folderPath });
      if (result.success) {
        set((s) => ({
          blueprintDataTablesByFolder: {
            ...s.blueprintDataTablesByFolder,
            [folderPath]: result.metadata.blueprintDataTables ?? [],
          },
        }));
      }
    } catch {
      /* best-effort — toolbar falls back to its own local state */
    }
  },
}));

/**
 * Selector hook that returns the live `blueprintDataTables` for `folderPath`.
 * Uses `useShallow` to avoid array-identity re-renders when the contents
 * are unchanged.
 */
export function useBlueprintDataTables(folderPath: string): BlueprintDataTable[] {
  return useBlueprintStateStore(
    useShallow((s) => s.blueprintDataTablesByFolder[folderPath] ?? []),
  );
}
